import React, { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, RefreshCw, Printer as PrinterIcon } from 'lucide-react';
import { PrintJobDto, printingApi } from '../../../services/api';
import { PrintJobItem } from './PrintJobItem';
import { Dialog } from '../../../components/Dialog';

interface PrintJobsModalProps {
  printerId: string;
  printerName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const PrintJobsModal: React.FC<PrintJobsModalProps> = ({
  printerId,
  printerName,
  isOpen,
  onClose,
}) => {
  const [jobs, setJobs] = useState<PrintJobDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchJobs();
    }
  }, [isOpen, printerId]);

  const fetchJobs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await printingApi.getJobs(printerId);
      setJobs(data);
    } catch (err: any) {
      console.error('Error fetching jobs:', err);
      setError(err.message || 'Error al cargar los trabajos de impresión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    try {
      await printingApi.deleteJob(jobId);
      // Refresh the jobs list
      await fetchJobs();
    } catch (err: any) {
      console.error('Error deleting job:', err);
      alert(err.message || 'Error al eliminar el trabajo');
      throw err;
    }
  };

  const handleDeleteAll = async () => {
    try {
      setIsDeletingAll(true);
      await printingApi.deleteAllJobs(printerId);
      setShowDeleteAllConfirm(false);
      // Refresh the jobs list
      await fetchJobs();
    } catch (err: any) {
      console.error('Error deleting all jobs:', err);
      alert(err.message || 'Error al eliminar todos los trabajos');
    } finally {
      setIsDeletingAll(false);
    }
  };

  return (
    <>
      <Dialog isOpen={isOpen} onClose={onClose} size="lg" noPadding>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <PrinterIcon size={20} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Cola de Impresión</h2>
              <p className="text-sm text-slate-600">{printerName}</p>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        {!isLoading && !error && jobs.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 bg-slate-50 border-b border-slate-200">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{jobs.length}</span>{' '}
              {jobs.length === 1 ? 'trabajo' : 'trabajos'} en cola
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchJobs}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Actualizar</span>
              </button>
              <button
                onClick={() => setShowDeleteAllConfirm(true)}
                disabled={isDeletingAll}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 size={14} />
                <span className="hidden sm:inline">Eliminar Todos</span>
                <span className="sm:hidden">Todos</span>
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto p-6 max-h-[60vh]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-4" />
              <p className="text-slate-600">Cargando trabajos...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle size={32} className="text-red-600" />
              </div>
              <p className="text-red-600 font-medium mb-2">Error al cargar los trabajos</p>
              <p className="text-slate-600 text-sm mb-4">{error}</p>
              <button
                onClick={fetchJobs}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Reintentar
              </button>
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <PrinterIcon className="h-16 w-16 mb-4" />
              <p className="text-lg font-medium">No hay trabajos en cola</p>
              <p className="text-sm mt-2">Esta impresora no tiene trabajos pendientes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => (
                <PrintJobItem 
                  key={job.id || job.jobId || Math.random().toString()} 
                  job={job} 
                  onDelete={handleDeleteJob}
                  onUpdate={fetchJobs}
                />
              ))}
            </div>
          )}
        </div>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog 
        isOpen={showDeleteAllConfirm} 
        onClose={() => setShowDeleteAllConfirm(false)}
        size="sm"
        noPadding
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Eliminar todos los trabajos</h3>
              <p className="text-sm text-slate-600">Esta acción no se puede deshacer</p>
            </div>
          </div>
          <p className="text-sm text-slate-700 mb-6">
            ¿Estás seguro de que quieres eliminar <span className="font-semibold">{jobs.length} {jobs.length === 1 ? 'trabajo' : 'trabajos'}</span> de la cola de impresión de <span className="font-medium">"{printerName}"</span>?
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowDeleteAllConfirm(false)}
              disabled={isDeletingAll}
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleDeleteAll}
              disabled={isDeletingAll}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDeletingAll ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Eliminar Todos
                </>
              )}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
